import React, { useState } from 'react';
import Table, { columnType } from '../../../components/ClearingTable/Table';
import Button from '../../../components/Buttons/Button';
import DateTimePicker from '../../../components/Textfields/DateTimePicker';
import AutoCompleteCorrespondent from '../../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteAccountNo from '../../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteAccountName from '../../../components/AutoComplete/AutoCompleteAccountName';
import AutoCompleteMasterAccountNo from '../../../components/AutoComplete/AutoCompleteMasterAccountNo';
import AutoCompleteBranch from '../../../components/AutoComplete/AutoCompleteBranch';
import AutoCompleteRep from '../../../components/AutoComplete/AutoCompleteRep';
import AutoCompleteSymbol from '../../../components/AutoComplete/AutoCompleteSymbol';
import Text from '../../../components/Typography/Text';
import {
  notifyInfo,
  notifyError,
  notifyWarning,
} from '../../../components/Messages/Notification';
import { listPosition } from '../../../services/PositionService';
import { getSystemDate } from '../../../services/ProfileService';
import QueryParam from '../../../services/QueryParamService';
import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderQty,
} from '../../../components/Table/CustomBodyRender';
import { formatPbDate } from '../../../lib/fmt';
import { formatQty, formatCurrency } from '../../../lib/fmt';
import { formatCurrencySymbol } from '../../../lib/fmt2';
import SelectSystemCode from '../../../components/Dropdown/SelectSystemCode';

export default function Position() {
  const [lockTable, setLockTable] = React.useState(true);
  const [footerData, setFooterData] = React.useState({});
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);
  const [pagination, setPagination] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [previousRows, setPreviousRows] = useState(0);
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      fromDate: '',
      toDate: '',
      correspondent: '',
      branch: '',
      accountNo: '',
      masterAccountNo: '',
      rep: '',
      accountName: '',
      symbol: '',
      dateType: 'Trade and Settle',
    })
  );

  const columns = [
    {
      name: 'dateType',
      label: 'Date Type',
    },
    {
      // Already converted to string in handlesearch
      name: 'date',
      label: 'Date',
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
    },
    {
      name: 'branch',
      label: 'Branch',
    },
    {
      name: 'accountNo',
      label: 'Account No',
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
    },
    {
      name: 'rep',
      label: 'Rep',
    },
    {
      name: 'accountName',
      label: 'Account Name',
    },
    {
      name: 'accountStatus',
      label: 'Account Status',
    },
    {
      name: 'symbol',
      label: 'Symbol',
    },
    {
      name: 'symbolDescription',
      label: 'Symbol Description',
    },
    {
      name: 'tdQty',
      label: 'Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: columnType.quantity,
    },
    {
      name: 'closingPrice',
      label: 'Closing Price',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      type: columnType.price,
    },
    {
      name: 'tdMarketValue',
      label: 'Market Value',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'costBasis',
      label: 'Cost Basis',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: columnType.amount,
    },
    {
      name: 'inTheMoney',
      label: 'In the Money',
      type: columnType.bool,
    },
    {
      name: 'itmOtmAmount',
      label: 'ITM/OTM Amount',
      type: columnType.amount,
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    serverSide: true,
    selectableRows: 'none',
    responsive: 'standard',
  };

  const init = async () => {
    const searchParameters = localStorage.getItem('search_data');

    const {
      fromDate,
      toDate,
      correspondent,
      branch,
      accountNo,
      masterAccountNo,
      rep,
      accountName,
      symbol,
    } = searchParameters ? JSON.parse(searchParameters) : searchData;

    const systemDate = await getSystemDate();

    setSearchData({
      ...searchData,
      fromDate: fromDate ? fromDate : systemDate,
      toDate: toDate ? toDate : systemDate,
      correspondent: correspondent ? correspondent : '',
      branch: branch ? branch : '',
      accountNo: accountNo ? accountNo : '',
      masterAccountNo: masterAccountNo ? masterAccountNo : '',
      rep: rep ? rep : '',
      accountName: accountName ? accountName : '',
      symbol: symbol ? symbol : '',
    });
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line
  }, []);

  const handleSearchDataChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({ ...searchData, [input.name]: input.value });
  };

  const handleSearch = async (paginationChange) => {
    let valid = true;

    if (!searchData.fromDate) {
      notifyWarning('From Date is required.');
      valid = false;
    }

    if (!searchData.toDate) {
      notifyWarning('To Date is required.');
      valid = false;
    }

    if (!valid) {
      return;
    }

    try {
      setLoading(true);
      if (paginationChange !== true) {
        setLockTable(false);
      }
      if (paginationChange === true) {
        if (lockTable === true) {
          return;
        }
      }
      QueryParam.set(searchData);
      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };
      if (previousRows !== 0) {
        if (previousRows < pagination.rowsPerPage * pagination.pageNo) {
          paginationCopy.pageNo = 0;
        }
      }
      if (paginationChange !== true) {
        paginationCopy.filterNames = [];
        paginationCopy.filterValues = [];
      }
      localStorage.setItem('search_data', JSON.stringify(searchData));
      const data = await listPosition(searchData, paginationCopy);
      let rowsCopy = data.positionsList;
      const rowsCopyOutput = rowsCopy.map((data) => {
        let tempDate = formatPbDate(data.date);
        data.date = tempDate;

        return data;
      });
      setSearchDataCsv(searchData);
      setTotalRows(data.summary.totalRows);
      setPreviousRows(totalRows);
      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);
      setRows(rowsCopyOutput);
      if (paginationChange !== true) {
        notifyInfo(data.summary.totalRows + ' search results.');
      }
      setFooterData({
        tdQty: formatQty(data.summary.tdQty),
        tdMarketValue: formatCurrency(data.summary.tdMarketValue),
        costBasis: formatCurrency(data.summary.costBasis),
      });
    } catch (error) {
      setLoading(false);
      notifyError(error.message);
      console.log(error);
      return;
    } finally {
      setLoading(false);
    }
  };

  const getCsvData = async () => {
    if (lockTable === true) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };
    const data = await listPosition(searchDataCsv, paginationCopy);

    const csvData = data.positionsList.map((item) => {
      let tempDate = formatPbDate(item.date);
      let tempExpDate = formatPbDate(item.expirationDate);
      item.date = tempDate;
      item.expirationDate = tempExpDate;
      item.qty = formatQty(item.tdQty);
      item.marketValue = formatCurrencySymbol(item.tdMarketValue);
      delete item.tdMarketValue;
      delete item.tdQty;
      return item;
    });

    return csvData;
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Position" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              type="search"
              label={loading ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <SelectSystemCode
              name="dateType"
              label="Date Type"
              type="Date Type"
              subType="Position Report"
              disableBlank
              value={searchData.dateType}
              onChange={handleSearchDataChange}
            ></SelectSystemCode>
          </div>
          <div className="grd-cell">
            <DateTimePicker
              type="date"
              name="fromDate"
              label="From Date"
              value={searchData.fromDate}
              onChange={handleSearchDataChange}
              required={true}
              inputProps={{ max: searchData.toDate }}
            />
          </div>
          <div className="grd-cell">
            <DateTimePicker
              type="date"
              name="toDate"
              label="To Date"
              value={searchData.toDate}
              onChange={handleSearchDataChange}
              required={true}
              inputProps={{ min: searchData.fromDate }}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountNo
              name="accountNo"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <AutoCompleteAccountName
              name="accountName"
              value={searchData.accountName}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              name="masterAccountNo"
              value={searchData.masterAccountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteBranch
              name="branch"
              value={searchData.branch}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteRep
              name="rep"
              value={searchData.rep}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteSymbol
              name="symbol"
              value={searchData.symbol}
              onChange={handleSearchDataChange}
            />
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Position'}
          data={rows}
          columns={columns}
          options={options}
          getCsvData={getCsvData}
          pagination={pagination}
          setPagination={setPagination}
          onSearch={handleSearch}
          footerData={footerData}
        />
      </div>
    </form>
  );
}
